import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';

import QAmountField from 'components/QAmountField';

import InvestmentTransactionBaseForm from './InvestmentTransactionBaseForm';
import type { InvestmentTransactionFormikProps } from './types';

const InvestmentTransactionBuyBondsForm = (props: InvestmentTransactionFormikProps) => {
  const { formik } = props;

  const [pricePerUnit, setPricePerUnit] = useState<number>();
  const [accruedInterest, setAccruedInterest] = useState<number>();

  useEffect(() => {
    formik.registerField('amount', { validate: (value: number) => (value ? undefined : 'please enter valid amount') });
    formik.registerField('units', {
      validate: (value: number | null | undefined) =>
        typeof value === 'number' && value ? undefined : 'field is required',
    });
    return () => {
      formik.unregisterField('amount');
      formik.unregisterField('units');
    };
  }, [formik]);

  const { setFieldValue } = formik;
  useEffect(() => {
    const commission = typeof formik.values.commission === 'number' ? formik.values.commission : 0;
    const units = typeof formik.values.units === 'number' ? Math.abs(formik.values.units) : 0;
    let costBasis = typeof formik.values.costBasis === 'number' ? formik.values.costBasis : 0;

    if (!accruedInterest && !pricePerUnit && formik.values.amount && costBasis) {
      const newAccruedInterest = formik.values.amount - costBasis;
      if (newAccruedInterest !== accruedInterest) {
        setAccruedInterest(newAccruedInterest);
      }
    }

    if (pricePerUnit) {
      costBasis = -(units * pricePerUnit - commission);
      if (formik.values.costBasis !== costBasis) {
        setFieldValue('costBasis', costBasis);
      }
    }

    if (!pricePerUnit && costBasis && units) {
      const newPricePerUnit = (Math.abs(costBasis) + commission) / units;
      if (pricePerUnit !== newPricePerUnit) {
        setPricePerUnit(newPricePerUnit);
      }
    }

    if (costBasis) {
      const amount = costBasis + (accruedInterest || 0);
      if (formik.values.amount !== amount) {
        setFieldValue('amount', amount);
      }
    }
  }, [
    setFieldValue,
    formik.values.amount,
    formik.values.units,
    pricePerUnit,
    accruedInterest,
    formik.values.commission,
    formik.values.costBasis,
  ]);

  return (
    <>
      <InvestmentTransactionBaseForm formik={formik} />

      <Box display='flex' gap={1}>
        <Box display='flex' flexDirection='column' gap={3}>
          <Box display='flex' alignItems='center' gap={1}>
            <QAmountField
              id='face-value'
              editable
              label='Face value'
              sx={{ minWidth: 110 }}
              noNegatives
              value={Math.abs(formik.values.units ?? 0) * 100}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                formik.setFieldValue('units', Number(event.target.value) / 100)
              }
              error={Boolean(formik.submitCount > 0 && formik.errors.units)}
              helperText={(formik.submitCount > 0 && formik.errors.units) || ''}
            />
            /
            <Box component='span' whiteSpace='nowrap'>
              100
            </Box>
          </Box>
        </Box>

        <Box display='flex' flexDirection='column' gap={3}>
          <Box display='flex' gap={1} alignItems='center'>
            =
            <TextField
              id='shares'
              label='Shares'
              type='number'
              inputProps={{ min: 1 }}
              sx={{ minWidth: 110 }}
              disabled
              value={Math.abs(formik.values.units ?? 0)}
            />
            *
            <QAmountField
              id='price-per-share'
              editable
              sx={{ minWidth: 110 }}
              label='Price (per $100)'
              noNegatives
              value={pricePerUnit}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPricePerUnit(Number(event.target.value))}
              error={Boolean(formik.submitCount > 0 && formik.errors.amount)}
              helperText={(formik.submitCount > 0 && formik.errors.amount) || ''}
            />
            +
            <QAmountField
              id='commission'
              editable
              sx={{ minWidth: 110 }}
              value={formik.values.commission ?? undefined}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                formik.setFieldValue('commission', Number(event.target.value))
              }
              label='Commission'
            />
          </Box>

          <Box display='flex' alignItems='center' gap={1}>
            =
            <QAmountField
              id='cost-basis'
              editable
              disabled
              InputProps={{
                readOnly: true,
              }}
              sx={{ minWidth: 110 }}
              label='Principal/cost basis'
              value={formik.values.costBasis ?? undefined}
            />
            +
            <QAmountField
              id='accrued-interest'
              editable
              sx={{ minWidth: 110 }}
              label='Accrued interest'
              value={accruedInterest}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAccruedInterest(Number(event.target.value))}
            />
          </Box>

          <Divider />

          <Box display='flex' gap={1}>
            =
            <QAmountField
              id='total-amount'
              editable
              disabled
              label='Total amount'
              sx={{ minWidth: 110 }}
              noNegatives
              value={Math.abs(formik.values.amount)}
              // onChange={(event) => formik.setFieldValue('amount', Number(event.target.value))}
              // error={Boolean(formik.submitCount > 0 && formik.errors.amount)}
              // helperText={(formik.submitCount > 0 && formik.errors.amount) || ''}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default InvestmentTransactionBuyBondsForm;
